import axios from "axios";
import { BASE_URL } from "../_constants/urlConstants";

const NWS_BASE = "https://api.weather.gov";
const IEM_ASOS_URL = "https://mesonet.agron.iastate.edu/cgi-bin/request/asos.py";

// All third-party calls (NWS, Nominatim, IEM) are routed through backend proxy endpoints
// rather than fetched directly from the browser: NWS and Nominatim both require the
// caller to identify itself via a proper User-Agent header, which fetch()/XHR cannot
// set from within the browser (Nominatim's usage policy explicitly forbids stock
// browser User-Agents). The backend attaches the identifying header before forwarding.
async function nwsProxyGet(nwsUrl) {
  const res = await axios.get(`${BASE_URL}/api/AssistantDailyReports/NwsProxy`, {
    params: { url: nwsUrl },
  });
  return res.data;
}

async function iemProxyGet(iemUrl) {
  const res = await axios.get(`${BASE_URL}/api/AssistantDailyReports/IemProxy`, {
    params: { url: iemUrl },
  });
  return res.data;
}

export async function geocodeAddress(address) {
  let data;
  try {
    const res = await axios.get(`${BASE_URL}/api/AssistantDailyReports/GeocodeAddress`, {
      params: { address },
    });
    data = res.data;
  } catch (e) {
    throw new Error("Geocoding service unavailable.");
  }
  if (!data.length) throw new Error("Location not found. Try a more specific address, city, or ZIP.");
  return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
}

async function getGridPoint(lat, lon) {
  let data;
  try {
    data = await nwsProxyGet(`${NWS_BASE}/points/${lat.toFixed(4)},${lon.toFixed(4)}`);
  } catch (e) {
    throw new Error("Location not supported by NWS. Must be within the United States.");
  }
  return {
    forecastUrl: data.properties.forecast,
    observationStationsUrl: data.properties.observationStations,
    timeZone: data.properties.timeZone,
  };
}

async function getStationId(observationStationsUrl) {
  const stData = await nwsProxyGet(observationStationsUrl);
  const stations = stData.features || [];
  if (!stations.length) throw new Error("No observation stations found near this location.");
  return stations[0].properties.stationIdentifier;
}

function celsiusToFahrenheit(c) {
  return Math.round(c * 9 / 5 + 32);
}

// Maps NWS text description to app's weatherCondition enum:
// 0=Clear, 1=Overcast, 2=Foggy, 3=Rain, 4=Snow
export function mapCondition(description) {
  if (!description) return null;
  const d = description.toLowerCase();
  if (d.includes("snow") || d.includes("sleet") || d.includes("blizzard") || d.includes("ice pellet")) return 4;
  if (d.includes("rain") || d.includes("drizzle") || d.includes("shower") || d.includes("thunder") || d.includes("storm")) return 3;
  if (d.includes("fog") || d.includes("mist") || d.includes("haze")) return 2;
  if (d.includes("overcast") || d.includes("cloudy")) return 1;
  if (d.includes("clear") || d.includes("sunny") || d.includes("fair")) return 0;
  return null;
}

// Picks the description whose timestamp is closest to local midday for the report date,
// rather than a positional midpoint (observations are unevenly spaced and often returned
// newest-first, so a positional midpoint doesn't reliably land near noon).
// 20:00Z is used as a fixed approximation of Pacific-time midday (accurate within the
// PST/PDT offset, which is acceptable for picking a representative condition description).
function pickMiddayEntry(entries, dateStr) {
  const targetMs = new Date(`${dateStr}T20:00:00Z`).getTime();
  let best = null;
  let bestDiff = Infinity;
  for (const entry of entries) {
    if (!entry.timestamp || !entry.description) continue;
    const diff = Math.abs(new Date(entry.timestamp).getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = entry.description;
    }
  }
  return best || "";
}

async function fetchFromObservations(stationId, date) {
  // Query from noon UTC (4am PST / 5am PDT) through the following noon UTC to cover CA workday
  const dateStr = date.toISOString().split("T")[0];
  const nextDate = new Date(date);
  nextDate.setUTCDate(nextDate.getUTCDate() + 1);
  const nextDateStr = nextDate.toISOString().split("T")[0];

  const url = `${NWS_BASE}/stations/${stationId}/observations?start=${dateStr}T12:00:00Z&end=${nextDateStr}T12:00:00Z`;
  const obsData = await nwsProxyGet(url);
  const observations = obsData.features || [];

  if (!observations.length) {
    throw new Error("No observations found for this date. NWS historical data is limited to approximately the last 7 days.");
  }

  const validTemps = observations
    .map(o => o.properties.temperature?.value)
    .filter(t => t !== null && t !== undefined);

  const highTemp = validTemps.length ? Math.max(...validTemps.map(celsiusToFahrenheit)) : "";
  const lowTemp = validTemps.length ? Math.min(...validTemps.map(celsiusToFahrenheit)) : "";

  const description = pickMiddayEntry(
    observations.map(o => ({ timestamp: o.properties.timestamp, description: o.properties.textDescription })),
    dateStr
  );

  return { highTemp, lowTemp, weatherCondition: mapCondition(description), sourceDescription: description };
}

async function fetchFromForecast(forecastUrl, date, timeZone) {
  const data = await nwsProxyGet(forecastUrl);
  const periods = data.properties?.periods || [];

  // Match periods using the grid point's own timezone (returned by NWS), not the browser's
  // local timezone — the two only coincide when the user's machine happens to be set to the
  // same timezone as the project site.
  const dateStr = new Intl.DateTimeFormat("en-CA", { timeZone }).format(date);
  const dayPeriod = periods.find(p => p.isDaytime && p.startTime.startsWith(dateStr));
  const nightPeriod = periods.find(p => !p.isDaytime && p.startTime.startsWith(dateStr));

  if (!dayPeriod && !nightPeriod) throw new Error("No forecast available for the selected date.");

  // Forecast temperatures are already in °F for US locations
  const highTemp = dayPeriod ? dayPeriod.temperature : "";
  const lowTemp = nightPeriod ? nightPeriod.temperature : "";
  const description = dayPeriod?.shortForecast || nightPeriod?.shortForecast || "";

  return { highTemp, lowTemp, weatherCondition: mapCondition(description), sourceDescription: description };
}

// Builds a description string out of METAR-style sky/weather codes using the same words
// mapCondition() already looks for (rain/snow/fog/cloudy/overcast/clear), so no separate
// condition-mapping table is needed for this source.
function describeIemRow(skyc1, wxcodes) {
  const codes = (wxcodes || "").toUpperCase();
  if (codes.includes("SN") || codes.includes("PL") || codes.includes("IP")) return "Snow";
  if (codes.includes("RA") || codes.includes("DZ") || codes.includes("TS")) return "Rain";
  if (codes.includes("FG") || codes.includes("BR") || codes.includes("HZ")) return "Fog";
  switch ((skyc1 || "").toUpperCase()) {
    case "OVC": return "Overcast";
    case "BKN": return "Mostly Cloudy";
    case "SCT":
    case "FEW": return "Partly Cloudy";
    case "CLR":
    case "SKC": return "Clear";
    default: return "";
  }
}

function parseIemCsv(csvText) {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) return [];
  const header = lines[0].split(",");
  const idx = {
    valid: header.indexOf("valid"),
    tmpf: header.indexOf("tmpf"),
    skyc1: header.indexOf("skyc1"),
    wxcodes: header.indexOf("wxcodes"),
  };
  return lines.slice(1).map((line) => {
    const cols = line.split(",");
    return {
      valid: cols[idx.valid],
      tmpf: cols[idx.tmpf],
      skyc1: cols[idx.skyc1],
      wxcodes: cols[idx.wxcodes],
    };
  });
}

// Fallback for report dates outside NWS's ~7-day observation window: IEM (Iowa Environmental
// Mesonet) maintains a long-term archive of the same ASOS station network NWS resolves a
// stationId from, so it's queried with the same identifier.
async function fetchFromIemArchive(stationId, date) {
  const dateStr = date.toISOString().split("T")[0];
  const nextDate = new Date(date);
  nextDate.setUTCDate(nextDate.getUTCDate() + 1);
  const nextDateStr = nextDate.toISOString().split("T")[0];
  const windowStartMs = new Date(`${dateStr}T12:00:00Z`).getTime();
  const windowEndMs = new Date(`${nextDateStr}T12:00:00Z`).getTime();

  // IEM's day2 param is exclusive, so request a day of padding on each side of the
  // noon-to-noon window above and filter down to it client-side.
  const rangeStart = new Date(date);
  rangeStart.setUTCDate(rangeStart.getUTCDate() - 1);
  const rangeEnd = new Date(date);
  rangeEnd.setUTCDate(rangeEnd.getUTCDate() + 2);

  const params = new URLSearchParams({
    station: stationId,
    data: "tmpf,skyc1,wxcodes",
    year1: rangeStart.getUTCFullYear(),
    month1: rangeStart.getUTCMonth() + 1,
    day1: rangeStart.getUTCDate(),
    year2: rangeEnd.getUTCFullYear(),
    month2: rangeEnd.getUTCMonth() + 1,
    day2: rangeEnd.getUTCDate(),
    tz: "Etc/UTC",
    format: "onlycomma",
    latlon: "no",
    elev: "no",
    missing: "M",
    trace: "T",
    direct: "no",
    report_type: "3",
  });

  const csvText = await iemProxyGet(`${IEM_ASOS_URL}?${params.toString()}`);
  const rows = parseIemCsv(csvText).filter((row) => {
    if (!row.valid) return false;
    const ms = new Date(row.valid.replace(" ", "T") + "Z").getTime();
    return ms >= windowStartMs && ms < windowEndMs;
  });

  if (!rows.length) {
    throw new Error("No historical observations found for this date in IEM's archive.");
  }

  const validTemps = rows
    .map(r => parseFloat(r.tmpf))
    .filter(t => !Number.isNaN(t));

  const highTemp = validTemps.length ? Math.round(Math.max(...validTemps)) : "";
  const lowTemp = validTemps.length ? Math.round(Math.min(...validTemps)) : "";

  const description = pickMiddayEntry(
    rows.map(r => ({
      timestamp: r.valid.replace(" ", "T") + "Z",
      description: describeIemRow(r.skyc1, r.wxcodes),
    })),
    dateStr
  );

  return { highTemp, lowTemp, weatherCondition: mapCondition(description), sourceDescription: description };
}

export async function fetchWeatherForDate(lat, lon, reportDate) {
  const { forecastUrl, observationStationsUrl, timeZone } = await getGridPoint(lat, lon);

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const rDate = new Date(reportDate);
  const reportDay = new Date(reportDate);
  reportDay.setHours(0, 0, 0, 0);

  // Today's observations only cover what's happened so far, so a morning fetch would report
  // the day's high as whatever the temperature has reached by then rather than the actual
  // forecasted high — use the forecast instead, which already reflects the full day's expected range.
  if (reportDay.getTime() === todayStart.getTime()) {
    return await fetchFromForecast(forecastUrl, rDate, timeZone);
  }

  if (reportDay.getTime() < todayStart.getTime()) {
    const stationId = await getStationId(observationStationsUrl);
    try {
      return await fetchFromObservations(stationId, rDate);
    } catch (e) {
      // NWS only keeps a rolling ~7-day observation window; fall back to IEM's long-term
      // ASOS archive for the same station before giving up.
      try {
        return await fetchFromIemArchive(stationId, rDate);
      } catch (iemError) {
        throw new Error(`${e.message} IEM archive fallback also failed: ${iemError.message}`);
      }
    }
  }

  return await fetchFromForecast(forecastUrl, rDate, timeZone);
}
